import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { FlaskConical, LogOut } from 'lucide-react';
import { labApi } from '@/components/lab/labApi';
import { LAB_INFO } from '@/components/lab/labPrint';
import { LabBillForm } from '@/components/lab/LabBillForm';
import { LabBillsList } from '@/components/lab/LabBillsList';
import { LabSummary } from '@/components/lab/LabSummary';
import { LabTestsManager } from '@/components/lab/LabTestsManager';

const istToday = () => new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
const TABS = [
  { id: 'today', label: "Today's Bills" },
  { id: 'dues', label: 'Pending Dues' },
  { id: 'summary', label: 'Tally' },
  { id: 'tests', label: 'Tests & Rates' },
];

export default function LabDashboard() {
  const { token, logout } = useAuth();
  const api = useMemo(() => labApi(token), [token]);
  const [tab, setTab] = useState('today');
  const [tests, setTests] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [bills, setBills] = useState([]);
  const [dues, setDues] = useState([]);
  const [tick, setTick] = useState(0);

  const loadTests = useCallback(() => api.get('/tests').then(setTests).catch(() => {}), [api]);
  const refresh = useCallback(() => {
    api.get('/bills').then(setBills).catch(() => {});
    api.get('/bills', { due_only: true }).then(setDues).catch(() => {});
    api.get('/referrals').then(setReferrals).catch(() => {});
    setTick((t) => t + 1);
  }, [api]);

  useEffect(() => { loadTests(); refresh(); }, [loadTests, refresh]);

  return (
    <div className="min-h-screen bg-slate-100" data-testid="lab-dashboard">
      <header className="bg-teal-800 text-white">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FlaskConical className="h-7 w-7" />
            <div>
              <h1 className="text-xl font-black tracking-wide" data-testid="lab-title">{LAB_INFO.name}</h1>
              <p className="text-xs text-teal-100 hidden sm:block">{LAB_INFO.address} • {LAB_INFO.phone}</p>
            </div>
          </div>
          <Button variant="ghost" className="text-white hover:bg-teal-700" onClick={logout} data-testid="lab-logout"><LogOut className="h-4 w-4 mr-2" /> Logout</Button>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-5 grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-5 min-w-0">
          <LabBillForm api={api} tests={tests} referrals={referrals} onSaved={refresh} />
        </div>
        <div className="lg:col-span-7 space-y-3 min-w-0">
          <div className="flex gap-1.5 flex-wrap">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`lab-tab-${t.id}`}
                className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${tab === t.id ? 'bg-teal-700 text-white border-teal-700' : 'bg-white text-slate-700'}`}>
                {t.label}{t.id === 'dues' && dues.length > 0 && <span className="ml-1.5 bg-red-500 text-white text-[10px] px-1.5 rounded-full">{dues.length}</span>}
              </button>
            ))}
          </div>
          {tab === 'today' && <LabBillsList bills={bills} api={api} onChange={refresh} title="Today's Bills" empty="No bills yet today" />}
          {tab === 'dues' && <LabBillsList bills={dues} api={api} onChange={refresh} title="Pending Dues" showDate empty="No pending balances" />}
          {tab === 'summary' && <LabSummary api={api} today={istToday()} refreshKey={tick} />}
          {tab === 'tests' && <LabTestsManager api={api} tests={tests} onChange={loadTests} />}
        </div>
      </main>
    </div>
  );
}
